import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { IsActiveDocContext } from '@/contexts/IsActiveDocContext';
import JsonLd from '@/components/JsonLd';
import {
  ORIGIN,
  buildBreadcrumbs,
  buildBreadcrumbList,
  buildTechArticle,
  buildCollectionPage,
  buildDataset,
  buildOrganizationPage,
  buildSoftwareApplication,
  buildProductsCollection,
} from '@/lib/schema';
import { SECTION_META } from '@/lib/docSections';

interface SEOMetadataProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
}

const SITE_NAME = 'AxionQuant';
const DEFAULT_OG_IMAGE = 'https://axionquant.com/meta.jpg?v=3';

/**
 * Strip any trailing brand suffix callers may have embedded ("- AxionQuant",
 * "- AxionQuant Blog", "- AxionQuant Docs", "- AxionQuant API Documentation",
 * "| AxionQuant", …) so the brand is appended exactly once below.
 */
function dedupeBrandSuffix(title: string): string {
  let out = title.trim();
  let prev = '';
  while (out !== prev) {
    prev = out;
    out = out
      .replace(/\s*[-|–—]\s*AxionQuant(\s+(Docs|Blog|API Documentation))?$/i, '')
      .trim();
  }
  return out;
}

function updateMetaDescription(description: string) {
  let el = document.querySelector('meta[name="description"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'description');
    el.setAttribute('content', description);
    document.head.appendChild(el);
  }
  el.setAttribute('content', description);
}

/**
 * Auto-derived structured data from the canonical path. Covers every route
 * shape that flows through SEOMetadata without per-template edits:
 *   - BreadcrumbList on any page with a meaningful trail
 *   - TechArticle on docs leaf pages, CollectionPage on docs section hubs
 *   - Dataset on /products/* pages (alongside their existing Product block)
 */
function buildSchemaBlocks(
  canonicalUrl: string | undefined,
  title: string,
  description: string
): Record<string, unknown>[] {
  if (!canonicalUrl) return [];

  const path = canonicalUrl.replace(/^https?:\/\/[^/]+/, '') || '/';
  const blocks: Record<string, unknown>[] = [];
  let m: RegExpMatchArray | null;

  const crumbs = buildBreadcrumbs(path, title);
  if (crumbs && crumbs.length >= 2) {
    blocks.push(buildBreadcrumbList(crumbs));
  }

  if (path === '/' || path === '') {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'AxionQuant',
      alternateName: ['Axion', 'Axion Quant'],
      url: 'https://axionquant.com',
      logo: 'https://axionquant.com/web-app-manifest-512x512.png',
      sameAs: [
        'https://medium.com/@axionquant',
        'https://www.instagram.com/axionquant/',
        'https://x.com/axionQuant',
        'https://github.com/axionquant',
        'https://www.youtube.com/@AxionQuant',
      ],
    });
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'AxionQuant',
      url: 'https://axionquant.com',
      description: 'Market data optimized for long-horizon analysis, fundamentals, and research. Access global equity coverage, alternative data, and developer-first client libraries.',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: 'https://axionquant.com/search?q={search_term_string}',
        },
        'query-input': 'required name=search_term_string',
      },
    });
  }

  if ((m = path.match(/^\/docs\/?(.*)$/))) {
    const rest = m[1].replace(/\/+$/, '');
    if (!rest || SECTION_META[rest]) {
      blocks.push(buildCollectionPage(title, description, canonicalUrl));
    } else {
      blocks.push(buildTechArticle(title, description, canonicalUrl));
    }
  }

  if (path === '/stocks') {
    blocks.push(buildCollectionPage(title, description, canonicalUrl));
  }

  if (/^\/products\/[a-z-]+\/?$/.test(path)) {
    blocks.push(buildDataset(title, description, canonicalUrl));
  }

  if (path === '/products' || path === '/products/') {
    blocks.push(buildProductsCollection(title, description, canonicalUrl));
  }

  if (path === '/about' || path === '/about/') {
    blocks.push(buildOrganizationPage(description, canonicalUrl));
  }

  if (path === '/developers' || path === '/developers/') {
    blocks.push(buildSoftwareApplication(title, description, canonicalUrl));
  }

  return blocks;
}

const SEOMetadata: React.FC<SEOMetadataProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
}) => {
  const isActive = useContext(IsActiveDocContext);

  useEffect(() => {
    if (isActive === false) return;
    const siteTitle = `${dedupeBrandSuffix(title)} | ${SITE_NAME}`;
    window.seoMeta = {
      title,
      description,
      canonicalUrl,
      ogImage: ogImage || DEFAULT_OG_IMAGE,
      ogTitle: siteTitle,
      ogDescription: description,
      ogType,
    };
    updateMetaDescription(description);
  }, [title, description, canonicalUrl, ogImage, ogType, isActive]);

  if (isActive === false) return null;

  const siteTitle = `${dedupeBrandSuffix(title)} | ${SITE_NAME}`;
  const schemaBlocks = buildSchemaBlocks(canonicalUrl, title, description);

  return (
    <>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={description} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content={SITE_NAME} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content={ogType} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content={description} />
        {canonicalUrl && <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />}
      </Helmet>
      {schemaBlocks.map((block, i) => (
        <JsonLd key={i} data={block} />
      ))}
    </>
  );
};

export default SEOMetadata;

// Re-export for consumers that previously imported the origin constant.
export { ORIGIN };
